"use client";

import React, {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";

import getDeviceType from "@/app/utils/device/getDeviceType";
import { OptionsType, OptionType, type SelectOption } from "@/types";
import { useTheme } from "next-themes";
import { ArrowDownIcon, ArrowUpIcon, Cross, UpperVectorIcon } from "../Icons";
import { Modal } from "../modal";
import { useLocale, useTranslations } from "next-intl";

type Props = {
  className: string;
  placeholder?: string;
  dropdownIndicator?: boolean;
  isSearchable?: boolean;
  options: OptionsType;
  formatOptionLabel?: any;
  isClearable?: boolean;
  customComponents?: any;
  onClick?: (op: OptionType) => void;
  closeMenuOnSelect?: boolean;
  loading?: boolean;
  selectedOption?: OptionType;
  setSelectedOption?: Dispatch<SetStateAction<any>>;
  iconComp?: any;
  onInputChange?: (op: string) => void;
  async?: boolean;
  travellerOptions: SelectOption[];
};

export default function Dropdown(props: Props) {
  const {
    placeholder,

    options,
    formatOptionLabel,

    travellerOptions,
    onClick,
    selectedOption,
    setSelectedOption,
    iconComp = <></>,
  } = props;
  const { resolvedTheme } = useTheme();

  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isMobile = getDeviceType().isMobileType;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const t = useTranslations();
  const locale = useLocale();
  const handleToggle = () => {
    if (isMobile) {
      setIsModalOpen(true);
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (option: OptionType) => {
    if (onClick) {
      onClick(option);
    }
    if (setSelectedOption) {
      setSelectedOption(option);
    }
    // setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formattedSelected = travellerOptions
    ?.filter((item) => Number(item.value) > 0)
    .map((item) => {
      const count = Number(item.value);
      let label = item.label;

      // Handle singular/plural based on label and count
      if (label === t("HomePageFlights.adults") && count === 1)
        label = t("HomePageFlights.adults");
      else if (label === t("HomePageFlights.children") && count === 1)
        label = t("HomePageFlights.child");
      // else if (label === t("HomePageFlights.infants") && count === 1) label = t("HomePageFlights.infant");
      return `${count} ${label}`;
    })
    .join(", ");

  const sum = travellerOptions?.reduce(
    (sum, item) => sum + Number(item.value),
    0
  );
  const distinctNonZeroCount = travellerOptions?.filter(
    (item) => Number(item.value) > 0
  ).length;

  const hasSumGreaterThanTwoFromDifferentObjects =
    sum > 2 && distinctNonZeroCount > 1;

  // const formattedSelected = `${sum} ${sum < 2 ? "Person" : "Persons"}`;

  return (
    <>
      <Modal
        isOpen={isModalOpen && isMobile}
        width="w-full h-screen !rounded-none bg-lightwhite dark:bg-sleap-gray-950 !m-0 pt-8"
        height="!p-0 !px-4"
        outerClassName="overflow-y-scroll"
        innerClassName="m-0.5 rounded-2xl p-0"
        bgColor="bg-lightwhite dark:bg-sleap-gray-950"
      >
        {" "}
        <div className="flex justify-between items-center py-4">
          <h4 className="text-sleap-gray-950 dark:text-lightwhite font-bold font-unbounded text-lg mb-0 ">
            {t("Other.travellers")}
          </h4>
          <span
            className="p-2 rounded-full bg-[#E4E4E7] dark:bg-sleap-gray-600 cursor-pointer"
            onClick={() => setIsModalOpen(false)}
          >
            <Cross color={resolvedTheme === "dark" ? "#fff" : "#09090B"} />
          </span>{" "}
        </div>
        <div className="rounded-xl bg-lightwhite dark:bg-dark h-[80vh] flex flex-col">
          <>
            {options.map((option: OptionType, index) => (
              <div key={index} onClick={() => handleSelect(option)}>
                {formatOptionLabel(option)}
              </div>
            ))}
          </>
          <div className="dark:border border-sleap-gray-850 rounded-b-xl dark:bg-sleap-gray-850 bg-white shadow-confirm-btn px-4 fixed bottom-0 left-0 py-4 z-50 w-full">
            <div className="flex items-center justify-between w-full">
              <button
                className="pointer flex items-center primary-border px-3 py-2 rounded-xl gap-x-2 w-full mt-auto "
                onClick={() => setIsModalOpen(false)}
              >
                <h5 className="text-base font-semibold font-inter text-white mb-0">
                  {t("Other.confirm")}
                </h5>
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <div ref={dropdownRef} className="sm:relative sm:0">
        <div
          className="traveler-input-container md:w-full h-14 flex flex-row justify-between rounded-lg gap-0.5 px-4 border-[1px] 2xl:border-2 dark:border-input border-sleap-gray-400 items-center dark:bg-sleap-gray-950 bg-lightwhite dark:hover:border-white hover:border-sleap-gray-600 gap-x-2 sm:max-w-[215px]"
          onClick={handleToggle}
        >
          {iconComp}{" "}
          <div
            className={`sm:mx-0 mx-auto text-center w-11/12 overflow-x-auto whitespace-nowrap relative ${hasSumGreaterThanTwoFromDifferentObjects || locale == "nl" ? "sm:top-2" : ""}`}
          >
            <pre className="mb-0 font-medium font-inter text-base">
              {selectedOption?.value ? formattedSelected : placeholder}
            </pre>
          </div>
          <span className="w-6 pe-2">
            {isOpen ? (
              <ArrowUpIcon
                color={resolvedTheme === "dark" ? "offWhite" : "lightblack"}
              />
            ) : (
              <Fragment>
                <span className="dark:hidden block">
                  <ArrowDownIcon color={"lightblack"} />
                </span>
                <span className="dark:block hidden">
                  <ArrowDownIcon color={"offWhite"} />
                </span>
              </Fragment>
            )}
          </span>
        </div>
        {isOpen && !isMobile && (
          <div
            className={`absolute right-0 z-20 w-full sm:p-0.5 sm:pe-[2.25px] bg-gradient-to-r sm:from-primary sm:to-secondary sm:rounded-xl sm:h-fit h-full bg-dark`}
          >
            <div className="rounded-xl dark:bg-sleap-gray-950 bg-lightwhite">
              {options.map((option: OptionType, index) => (
                <div key={index} onClick={() => handleSelect(option)}>
                  {formatOptionLabel(option)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
